import { Flex } from '@mantine/core';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';

import type { Person } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { AvatarV2 as Avatar, Pill, TableColumnHeader } from '@feathr/components';
import { moment } from '@feathr/hooks';
import growthCreditIcon from '@feathr/report_components/images/growth_credit_icon.svg';

import { PersonName } from '../../ConversionTableCard';
import { EDonationEngagementFlavor } from './DonationFormInteractionSelect';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './donationFormEngagementColumns.css';

interface IRow {
  original: Person;
}

interface IPersonCellProps {
  original: Person;
  localUrl: ((url: string) => string) | undefined;
  t: TFunction;
}

interface IFirstFlavorHit {
  d_c?: string;
  donation?: { amount?: number; currency?: string; frequency?: string };
  growth_credits?: number;
}

function PersonCell({ localUrl, original, t }: Readonly<IPersonCellProps>): JSX.Element {
  return (
    <Observer>
      {function useAnonymousFunction(): JSX.Element {
        if (original) {
          return (
            <div className={styles.container}>
              <Avatar name={original.get('name')} placeholder={original.get('placeholder')} />
              <div className={styles.content}>
                <div className={styles.name}>
                  <PersonName localUrl={localUrl} person={original.toJS()} />{' '}
                  {original.isArchived && (
                    <Pill showPrefix={false} variant={'secondary'}>
                      {t('Archived')}
                    </Pill>
                  )}
                </div>
                {original.get('external_id') && <span>{original.get('external_id')}</span>}
              </div>
            </div>
          );
        }

        return <div className={styles.container}>Anonymous</div>;
      }}
    </Observer>
  );
}

function getFirstFlavorHit(person: Person): IFirstFlavorHit | undefined {
  const innerHits = person.get('inner_hits') as { first_flavor?: IFirstFlavorHit[] } | undefined;
  return innerHits?.first_flavor?.[0];
}

function donationFormEngagementColumns(
  t: TFunction,
  localUrl: ((url: string) => string) | undefined,
  flavor: EDonationEngagementFlavor,
): Array<IColumn<Person>> {
  const nameColumn: IColumn<Person> = {
    id: 'name',
    className: tableStyles.cell,
    Header: TableColumnHeader({
      sortType: 'alpha',
      title: t('Name'),
    }),
    headerClassName: tableStyles.sort,
    sortable: true,
    Cell({ original }: IRow): JSX.Element {
      return <PersonCell localUrl={localUrl} original={original} t={t} />;
    },
  };

  const donationColumn: IColumn<Person> = {
    id: 'donation',
    className: tableStyles.cellRight,
    headerClassName: tableStyles.cellRight,
    Header: TableColumnHeader({
      title: t('Donation'),
    }),
    sortable: false,
    Cell({ original }: IRow): JSX.Element {
      return (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const amount = getFirstFlavorHit(original)?.donation?.amount;
            if (typeof amount !== 'number') {
              return <>{t('N/A')}</>;
            }
            return <>{numeral(amount).format('$0,0.00')}</>;
          }}
        </Observer>
      );
    },
  };

  const outreachColumn: IColumn<Person> = {
    id: 'outreach_support',
    className: tableStyles.cellRight,
    headerClassName: tableStyles.cellRight,
    Header: TableColumnHeader({
      title: t('Outreach Support'),
    }),
    sortable: false,
    Cell({ original }: IRow): JSX.Element {
      return (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const credits = getFirstFlavorHit(original)?.growth_credits;
            if (typeof credits !== 'number') {
              return <>{t('N/A')}</>;
            }
            return (
              <Flex align={'center'} className={styles.outreachCell}>
                {numeral(credits).format('0,0')}
                <img alt={t('Growth credit icon')} src={growthCreditIcon} />
              </Flex>
            );
          }}
        </Observer>
      );
    },
  };

  const interactionDateColumn: IColumn<Person> = {
    id: 'first_interacted',
    className: tableStyles.cellCenter,
    Header: TableColumnHeader({
      title: t('First interacted'),
    }),
    sortable: false,
    Cell({ original }: IRow): JSX.Element {
      return (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const firstCrumbDate = getFirstFlavorHit(original)?.d_c;
            if (!firstCrumbDate) {
              return <>—</>;
            }
            return <>{moment.utc(firstCrumbDate).local().format('MMM D YYYY, h:mma')}</>;
          }}
        </Observer>
      );
    },
  };

  if (flavor === EDonationEngagementFlavor.ViewedNoDonation) {
    return [nameColumn, interactionDateColumn];
  }

  return [nameColumn, donationColumn, outreachColumn, interactionDateColumn];
}

export default donationFormEngagementColumns;
